import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";

import {
  authButton,
  backButton,
  bodyContainer,
  container,
  divider,
  subContainer,
  titleText,
} from "../Payment/styles";
import { colors } from "../../theme/colors";
import { db, functions } from "../../services/firebase";
import { OrderType, PublicKeyType } from "../../types";
import { showToast } from "../../services/toast";
import { useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";

interface PostData {
  [key: string]: any;
}
interface YocoSDK {
  showPopup: (options: {
    amountInCents: number;
    currency: string;
    name: string;
    description: string;
    onCancel: () => void;
    callback: (result: any) => void;
  }) => void;
}

declare global {
  interface Window {
    YocoSDK: new (options: { publicKey: string }) => YocoSDK;
  }
}

const ViewOrder = () => {
  const [isLoading, setIsLoading] = useState(false);
  const orderId = window.location.href.split("/").pop();
  const [order, setOrder] = useState<OrderType>();
  const [secretKey, setSecretKey] = useState<string>("");

  const navigate = useNavigate();
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const variable = params.get("variable");
  //const orderId = params.get("id");

  useEffect(() => {
    if (orderId) {
      getOrder();
    }
  }, [orderId]);

  useEffect(() => {
    getSecretKey();
  }, []);

  useEffect(() => {
    // Dynamically load Yoco script
    const script = document.createElement("script");
    script.src = "https://js.yoco.com/sdk/v1/yoco-sdk-web.js";
    script.async = true;
    script.style.marginTop = "1000px !important";
    script.onload = () => {
      console.log("Yoco loaded");
    };

    script.onerror = () => {
      console.log("Could not load Yoco");
    };

    document.body.appendChild(script);
    // Clean up the script element when the component is unmounted
    if (document.getElementById("CheckoutIframe")) {
      document.getElementById("CheckoutIframe")!.style.marginTop = "100px";
    }

    return () => {
      document.body.removeChild(script);
    };
  }, [document.getElementById("CheckoutIframe")]);

  const getSecretKey = async () => {
    const docRef = doc(db, `common/publicKeys`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      let publicKeys = docSnap.data() as PublicKeyType;
      setSecretKey(publicKeys.yoco || "");
    } else {
      // docSnap.data() will be undefined in this case
      console.log("No such document!");
      showToast("No such document!", "error");
      navigate(-1);
    }
  };

  const getOrder = async () => {
    setIsLoading(true);
    const docRef = doc(db, `sidorders/${orderId}/view/${orderId}`);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      setOrder(docSnap.data() as OrderType);
      console.log(docSnap.data());
    } else {
      // docSnap.data() will be undefined in this case
      console.log("No such document!");
      showToast("No such document!", "error");
      navigate(-1);

    }
    setIsLoading(false);
  };

  const payWithPaygate = () => {
    setIsLoading(true);
    const payForOrder = httpsCallable(functions, "payForOrder");
    payForOrder({
      id: orderId,
    })
      .then((result: any) => {
        console.log(result.data);
        const url = "https://secure.paygate.co.za/payweb3/process.trans";
        postURL(url, result.data);
        setIsLoading(false);
      })
      .catch((error) => {
        if (error instanceof FirebaseError) {
          showToast((error as FirebaseError).message, "error");
        } else {
          showToast("Unknown error", "error");
        }
        setIsLoading(false);
      });
  };

  const postURL = (url: string, data: PostData, multipart: boolean = false) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;
    if (multipart) {
      form.enctype = "multipart/form-data";
    }
    form.style.display = "none";
    document.body.appendChild(form);

    Object.keys(data).forEach((key) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = data[key];
      form.appendChild(input);
    });

    form.submit();
  };

  const payWithYoco = () => {
    setIsLoading(true);
    const yoco = new window.YocoSDK({
      publicKey: secretKey,
    });

    yoco.showPopup({
      amountInCents: order?.amount ? order.amount * 100 : 0,
      currency: "ZAR",
      name: "Payment",
      description: "Awesome description",
      onCancel: () => setIsLoading(false),
      callback: (result: any) => {
        if (result.error) {
          const errorMessage = result.error.message;
          alert("Error occurred: " + errorMessage);
        } else {
          const callData = {
            id: orderId,
            token: result.id,
          };
          const payForOrderWithYoco = httpsCallable(
            functions,
            "payForOrderWithYoco"
          );
          payForOrderWithYoco(callData)
            .then((res: any) => {
              showToast("Payment was done successfully.");
              setIsLoading(false);
              navigate("/myChecks");
            })
            .catch((error) => {
              if (error instanceof FirebaseError) {
                showToast((error as FirebaseError).message, "error");
              } else {
                showToast("Unknown error", "error");
              }
              setIsLoading(false);
            });
        }
      },
    });
  };

  return (
    <Box sx={(theme) => container(theme)}>
      <Box sx={(theme) => bodyContainer(theme)}>
        <Box sx={(theme) => subContainer(theme)}>
          <Box sx={{ pb: 15, width: "100%" }}>
            <Typography sx={(theme) => titleText(theme)}>
              {order && order.processed ? "Order paid and processed" : "Waiting for payment"}
            </Typography>
            {order && order.name && <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                  mr: 1,
                }}
              >
                • Fingerprint name:
              </Typography>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                {order.name}
              </Typography>

            </Box>}
             {order && order.surname && <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                  mr: 1,
                }}
              >
                • Surname:
              </Typography>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                {order.surname}
              </Typography>

            </Box>}
             {order && order.numberPlate && <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                  mr: 1,
                }}
              >
                • Number plate:
              </Typography>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                {order.numberPlate}
              </Typography>

            </Box>}
            <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                  mr: 1,
                }}
              >
                • Cost:
              </Typography>
              <Typography
                sx={{
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                {order && order.amount ? order.amount : ""}
              </Typography>
            </Box>
            {order && !order.processed && <span>
              <Divider sx={(theme) => divider(theme)}>pay with</Divider>
              <Button
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={(theme) => authButton(theme)}
                onClick={() => {
                  payWithYoco();
                }}
              >
                {"YOCO"}
                {isLoading && (
                  <CircularProgress size={30} sx={{ ml: 1, color: "white" }} />
                )}
              </Button>
              <Button
                fullWidth
                variant="contained"
                sx={(theme) => authButton(theme)}
                disabled={isLoading}
                onClick={() => {
                  payWithPaygate();
                }}
              >
                {"PAYGATE"}
                {isLoading && (
                  <CircularProgress size={30} sx={{ ml: 1, color: "white" }} />
                )}
              </Button>
            </span>}
          </Box>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: "12px",
              color: colors.dark_gray,
              mt: 2,
            }}
          >
            By clicking continue, you agree to our{" "}
            <b style={{ color: "black" }}>Terms of Service</b> and{" "}
            <b style={{ color: "black" }}>Privacy Policy</b>
          </Typography>
          <Box sx={{ width: "100%" }}>
            <Button
              variant="contained"
              sx={(theme) => backButton(theme)}
              onClick={() => {
                navigate(-1);
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ViewOrder;
